import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, Slide, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
// icons
import AngleRightIcon from "common/customIcons/angle_right";
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { deleteRegion, downloadBoxPDF, downloadDriverKitPDF, getDistributionBoxes, getDistributionKitRegions, getDistributionKitsBoxes, getLowStockProducts, resetDistribution } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from "../../../../redux/slices/common";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../../redux/slices/modalSlice";
// custom components
import { showToastMsg } from "../addRegionKit";
import MDButton from 'components/MDButton';
import LowStockProducts from "common/CommonDrawer/hyperwolf/lowStockProducts";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";
import ProductsListing from "../components/productsListing";
import CommonLayout from "layouts/DashboardLayout";
import { formatTimestampToWithoutTime } from "utilities/common";
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import { getData } from 'utilities/common';

export const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const DistributionDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { distributionId } = useParams();
  const [searchParams] = useSearchParams()
  const { distributionDate } = Object.fromEntries(searchParams.entries());

  const [distributionBoxes, setDistributionBoxes] = useState([]);
  const [allBoxes, setAllBoxes] = useState([]);
  const [productsBoxes, setProductsBoxes] = useState([]);
  const [regionKits, setRegionKits] = useState([]);
  const [lowStockDrawerOpen, setLowStockDrawerOpen] = useState(false);
  const [selectedKit, setSelectedKit] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [inputFilters, setInputFilters] = useState({});
  const [isLoadingRegionKits, setIsLoadingRegionKits] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingBoxPDF, setIsDownloadingBoxPDF] = useState(false);
  const [isDownloadingDriverPDF, setIsDownloadingDriverPDF] = useState(false);
  const [isResettingDistribution, setIsResettingDistribution] = useState(false);
  const [isFreezed, setIsFreezed] = useState(true);

  const handleSelectedKit = (region) => {
    setSelectedKit(region);
    setCustomFilters({});
    setInputFilters({});
    handleGetProductsBoxes({ regionId: region?.regionId });
    setProductsBoxes([]);
  };

  const handleRegionKits = async () => {
    setIsLoadingRegionKits(true);
    try {
      const response = await dispatch(getDistributionKitRegions({ distributionId })).unwrap();
      setIsFreezed(response?.freeze || false);
      const { data } = response || {};

      if (data) {
        setRegionKits(data || []);
        const region = data?.[0] || {};
        if (region?.regionId) {
          setSelectedKit(region);
          handleGetProductsBoxes({ regionId: region?.regionId })
        }
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoadingRegionKits(false);
    }
  };

  const handleGetProductsBoxes = async (filters = {}) => {
    setIsLoading(true);
    try {
      const response = await dispatch(getDistributionKitsBoxes({ ...filters, distributionId })).unwrap();
      if (response?.data) {
        setProductsBoxes(response?.data || []);
        setAllBoxes(response?.data || []);
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDriverPDF = async () => {
    if (isDownloadingDriverPDF || !selectedKit.regionId) return;
    setIsDownloadingDriverPDF(true);

    try {
      const response = await dispatch(downloadDriverKitPDF({ distributionId, regionId: selectedKit.regionId })).unwrap();

      if (response?.url) {
        const link = document.createElement("a");
        link.href = response.url;
        link.download = "driver_kit.pdf";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();

        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    } catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsDownloadingDriverPDF(false);
    }
  };

  const handleDownloadBoxPDF = async () => {
    if (isDownloadingBoxPDF || !selectedKit.regionId) return;
    setIsDownloadingBoxPDF(true);
    try {
      const response = await dispatch(downloadBoxPDF({ distributionId, regionId: selectedKit.regionId })).unwrap();
      if (response?.url) {
        // Create a temporary link to download the PDF
        const link = document.createElement('a');
        link.href = response.url;
        link.download = "driver_kit.pdf";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsDownloadingBoxPDF(false);
    }
  };

  const handleDeleteRegion = async (regionId) => {
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(deleteRegion({ distributionId, regionId })).unwrap();
      showToastMsg(res?.message, 'success');
      dispatch(closeCustomModalDialog());
      handleRegionKits()
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err?.message, 'error');
    };
  };

  const handleResetDistribution = async () => {
    if (isResettingDistribution) return;
    setIsResettingDistribution(true);
    try {
      const response = await dispatch(resetDistribution({ distributionId })).unwrap();
      if (response?.message) {
        showToastMsg(response.message || "Success", "success");
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsResettingDistribution(false);
    }
  };

  const handleDistributionBoxes = async () => {
    try {
      const response = await dispatch(getDistributionBoxes(distributionId)).unwrap();
      const { data } = response || {};
      if (data) {
        setDistributionBoxes(data || []);
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  useEffect(() => {
    handleRegionKits();
    handleDistributionBoxes();
  }, []);



  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  const canCreate = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Create");
  const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
  const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");

  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Distribution ({formatTimestampToWithoutTime(Number(distributionDate))})
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {/* Top actions */}
          <Grid item xs={12}>
            <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between" className="res-flex-col">
              <div className="d-flex align-item-top gap-2">

                {/* Low Stock Products */}
                {
                  canView &&
                  <MDButton
                    variant="contained"
                    sx={{ minWidth: '160px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                    onClick={() => setLowStockDrawerOpen(true)}
                  >
                    Low Stock Products
                  </MDButton>
                }


                {/*  Freeze Distribution */}
                {!isFreezed &&
                  <MDButton
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: '160px', fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                    onClick={() => {
                      dispatch(openCustomModalDialog({ modalData: { distributionBoxes, distributionId, handleRegionKits }, modal: "FREEZE_DISTRIBUTION" }))
                    }}
                  >
                    Freeze Distribution
                  </MDButton>}
              </div>
              <div className="d-flex align-item-top gap-2 res-flex-col">

                {/* Reset distribution */}
                {
                  canCreate || canEdit && <>
                    {!isFreezed && <MDButton
                      variant="contained"
                      sx={{ minWidth: '145px', background: "#ffffff !important", color: "#2E2E3A !important", fontSize: "14px", minHeight: "36px", border: "1px solid #D0D0DA", padding: "7px 20px" }}
                      className="d-flex align-item-center jc-center gap-1"
                      disabled={isResettingDistribution}
                      onClick={handleResetDistribution}
                    >
                      {isResettingDistribution ? <CircularProgress size={20} /> :
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1.33398C6.25296 1.3378 4.57722 2.02724 3.33333 3.25398V2.00065C3.33333 1.82384 3.2631 1.65427 3.13807 1.52925C3.01305 1.40422 2.84348 1.33398 2.66667 1.33398C2.48986 1.33398 2.32029 1.40422 2.19526 1.52925C2.07024 1.65427 2 1.82384 2 2.00065V5.33398C2 5.5108 2.07024 5.68036 2.19526 5.80539C2.32029 5.93041 2.48986 6.00065 2.66667 6.00065H6C6.17681 6.00065 6.34638 5.93041 6.4714 5.80539C6.59643 5.68036 6.66667 5.5108 6.66667 5.33398C6.66667 5.15717 6.59643 4.9876 6.4714 4.86258C6.34638 4.73756 6.17681 4.66732 6 4.66732H3.85333C4.54106 3.81815 5.47303 3.20072 6.52315 2.89856C7.57328 2.59641 8.69087 2.6241 9.72474 2.97791C10.7586 3.33171 11.6588 3.99455 12.3037 4.87674C12.9485 5.75893 13.3068 6.8179 13.33 7.91039C13.3533 9.00287 13.0404 10.0761 12.4338 10.985C11.8271 11.8938 10.9559 12.5944 9.93801 12.9919C8.92014 13.3894 7.80474 13.4646 6.7427 13.2075C5.68067 12.9503 4.72326 12.3731 4 11.554C3.94134 11.4888 3.87041 11.4357 3.79126 11.3979C3.71211 11.3601 3.62629 11.3383 3.53869 11.3336C3.45109 11.329 3.36344 11.3416 3.28073 11.3709C3.19803 11.4001 3.12189 11.4453 3.05667 11.504C2.99144 11.5626 2.93841 11.6336 2.9006 11.7127C2.86279 11.7919 2.84094 11.8777 2.8363 11.9653C2.83165 12.0529 2.84431 12.1405 2.87354 12.2233C2.90277 12.306 2.94801 12.3821 3.00667 12.4473C3.74758 13.2749 4.68354 13.9043 5.72951 14.2782C6.77549 14.6521 7.89831 14.7588 8.99597 14.5884C10.0936 14.4181 11.1313 13.9762 12.0148 13.3028C12.8983 12.6295 13.5995 11.7461 14.0547 10.7329C14.51 9.71965 14.7048 8.60873 14.6216 7.50106C14.5384 6.39338 14.1797 5.32406 13.5781 4.39025C12.9766 3.45643 12.1512 2.68773 11.177 2.15399C10.2029 1.62024 9.11079 1.33837 8 1.33398Z" fill="#2E2E3A" />
                          </svg>
                          <span>
                            Reset Distribution
                          </span>
                        </>
                      }
                    </MDButton>}
                  </>
                }

                {/* Download box pdf */}
                {
                  canView &&
                  <MDButton
                    variant="contained"
                    sx={{ minWidth: '150px', background: "#ffffff !important", color: "#2E2E3A !important", fontSize: "14px", minHeight: "36px", border: "1px solid #D0D0DA", padding: "7px 20px" }}
                    className="d-flex align-item-center jc-center gap-1"
                    disabled={isDownloadingBoxPDF || !selectedKit.regionId}
                    onClick={handleDownloadBoxPDF}
                  >
                    {
                      isDownloadingBoxPDF ?
                        <CircularProgress size={20} />
                        : <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 11V12.5C2 12.8978 2.15804 13.2794 2.43934 13.5607C2.72064 13.842 3.10218 14 3.5 14H12.5C12.8978 14 13.2794 13.842 13.5607 13.5607C13.842 13.2794 14 12.8978 14 12.5V11M11 8L8 11M8 11L5 8M8 11V2" stroke="#2E2E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Get Box PDF</span>
                        </>
                    }
                  </MDButton>
                }

                {/* Download driver kit pdf */}
                {
                  canView &&
                  <MDButton
                    variant="contained"
                    sx={{ minWidth: '185px', background: "#ffffff !important", color: "#2E2E3A !important", fontSize: "14px", minHeight: "36px", border: "1px solid #D0D0DA", padding: "7px 20px" }}
                    className="d-flex align-item-center jc-center gap-1"
                    disabled={isDownloadingDriverPDF || !selectedKit.regionId}
                    onClick={handleDownloadDriverPDF}
                  >
                    {isDownloadingDriverPDF ? <CircularProgress size={20} /> :
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 11V12.5C2 12.8978 2.15804 13.2794 2.43934 13.5607C2.72064 13.842 3.10218 14 3.5 14H12.5C12.8978 14 13.2794 13.842 13.5607 13.5607C13.842 13.2794 14 12.8978 14 12.5V11M11 8L8 11M8 11L5 8M8 11V2" stroke="#2E2E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Get Driver Kit PDF</span>
                      </>
                    }
                  </MDButton>
                }
              </div>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <div className="distribution-with-sidebar">
              {/* Left side section */}
              <div className="dis-sidebar-left">
                <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none", height: "100%" }}>
                  {/* Add Region btn */}
                  <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between">
                    <Typography variant="h5" fontWeight="semibold" style={{ whiteSpace: "nowrap" }}>Region Kits</Typography>
                    {!isFreezed && <MDButton
                      variant="contained"
                      color="primary"
                      sx={{ fontSize: "14px", minHeight: "36px", padding: "7px 20px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important", whiteSpace: "nowrap" }}
                      onClick={() => navigate(`/hyperwolf/distribution-management/add-region-kit/${distributionId}?distributionDate=${distributionDate}`)}
                    >
                      + Add Region
                    </MDButton>}
                  </Stack>

                  {/* Regions kits listing */}
                  {
                    isLoadingRegionKits ?
                      <TableLoader />
                      : <ul>
                        {
                          regionKits?.map((region) => (
                            <li
                              key={region.regionId}
                              className={selectedKit.regionId === region.regionId ? "active" : ""}
                              onClick={() => handleSelectedKit(region)}
                            >
                              <div className="d-flex align-item-center jc-s-btwn gap-1 mb-2">
                                <Typography variant="h6" fontWeight="medium" color="#2E2E3A">{region.regionName}</Typography>
                                {regionKits?.length > 1 && !isFreezed && <IconButton
                                  color='error'
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(openCustomModalDialog({
                                      modal: 'DELETE_MODAL',
                                      modalData: { deleteHandler: handleDeleteRegion, deleteId: region.regionId },
                                    }))
                                  }}
                                >
                                  <DeleteIcon width='16px' height='16px' />
                                </IconButton>}
                              </div>
                              <div className="d-grid grid-columns-one gap-2">
                                {/* {distributionConfigs.isRetailer && <div className="d-flex align-item-top flex-column gap-1">
                                  <Typography color="#656575">Retail Value</Typography>
                                  <span>${region?.totalRetailValue?.toLocaleString()}</span>
                                </div>} */}
                                {
                                  region?.subRegions?.length > 0 &&
                                  region?.subRegions?.map(subRegion => (
                                    <div key="" className="d-grid grid-columns-two gap-2-5" style={{ backgroundColor: "#f7f7f9", padding: "10px", borderRadius: "4px" }}>
                                      <div className="d-flex align-item-top flex-column gap-1">
                                        <Typography color="#656575">Sub Region</Typography>
                                        <span>{subRegion?.subRegionName || "-"}</span>
                                      </div>
                                      <div className="d-flex align-item-top flex-column gap-1">
                                        <Typography color="#656575">Kit Value</Typography>
                                        <span>${subRegion?.wholesaleValue?.toLocaleString()}</span>
                                      </div>
                                      <div className="d-flex align-item-top flex-column gap-1">
                                        <Typography color="#656575">Unique Products</Typography>
                                        <span>{subRegion?.totalProducts?.toLocaleString()}</span>
                                      </div>
                                      <div className="d-flex align-item-top flex-column gap-1">
                                        <Typography color="#656575">Total Qty</Typography>
                                        <span>{subRegion?.totalQtyDistributed?.toLocaleString()}</span>
                                      </div>
                                    </div>
                                  ))
                                }
                              </div>
                            </li>
                          ))
                        }
                      </ul>
                  }
                </Card>
              </div>

              <ProductsListing
                regionKits={regionKits}
                allBoxes={allBoxes}
                productsBoxes={productsBoxes}
                setProductsBoxes={setProductsBoxes}
                isLoading={isLoading}
                selectedKit={selectedKit}
                customFilters={customFilters}
                setCustomFilters={setCustomFilters}
                inputFilters={inputFilters}
                setInputFilters={setInputFilters}
                isFreezed={isFreezed}
              />
            </div>
          </Grid>
        </Grid>

        {/* low stock products */}
        <LowStockProducts
          open={Boolean(lowStockDrawerOpen)}
          TransitionComponent={Transition}
          handleClose={() => setLowStockDrawerOpen(false)}
          getDataMethod={getLowStockProducts}
          dataFilters={{ distributionId }}
        />
      </Box>
    </CommonLayout>
  )
}

export default DistributionDetails